/**
 * 消息管理API
 */
import type { AxiosRequestConfig } from 'axios'
import { request } from '@/shared/api/request'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import { ProjectType } from '@/api/common-types'
import { getGroupContacts } from './contact'

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

// 消息类型枚举
export enum MessageType {
  SYSTEM = 'system',
  USER = 'user',
  TASK_NOTIFICATION = 'task_notification',
  TASK_COMPLETION = 'task_completion'
}

// 用户摘要信息
export interface UserSummary {
  id: number
  username: string
  name: string
  name_remark?: string
  language?: string
}

// 消息响应
export interface MessageResponse {
  id: number
  sender_id?: number
  receiver_id: number
  message_type: MessageType
  original_content: string
  translated_content?: string
  original_language?: string
  target_language?: string
  is_read: boolean
  related_project_id?: number
  related_project_type?: ProjectType
  related_task_id?: number
  created_at: string
  sender?: UserSummary
  receiver?: UserSummary
}

// 消息列表响应
export interface MessageListResponse {
  items: MessageResponse[]
  total: number
  page: number
  page_size: number
  total_pages: number
  unread_count: number
}

// 发送消息请求
export interface SendMessageRequest {
  receiver_id: number
  content: string
  message_type?: MessageType
  related_project_id?: number
  related_project_type?: ProjectType
  related_task_id?: number
}

/**
 * 获取消息列表
 */
export const getMessages = (
  params: {
  page?: number
  page_size?: number
  message_type?: MessageType
  is_read?: boolean
},
  config?: RequestConfig,
): Promise<MessageListResponse> => {
  return request.get('/messages', { params, ...config })
}

/**
 * 获取消息详情
 */
export const getMessage = (messageId: number): Promise<MessageResponse> => {
  return request.get(`/messages/${messageId}`)
}

/**
 * 发送消息
 */
export const sendMessage = (messageData: SendMessageRequest): Promise<MessageResponse> => {
  return request.post('/messages', messageData)
}

/**
 * 标记消息为已读
 */
export const markMessageAsRead = (messageId: number): Promise<MessageResponse> => {
  return request.put(`/messages/${messageId}/read`)
}

/**
 * 批量标记消息为已读
 */
export const markMessagesAsRead = (messageIds: number[]): Promise<{ updated_count: number }> => {
  return request.put('/messages/batch-read', { message_ids: messageIds })
}

/**
 * 删除消息
 */
export const deleteMessage = (messageId: number): Promise<{ message: string }> => {
  return request.delete(`/messages/${messageId}`)
}

/**
 * 获取未读消息数量
 */
export const getUnreadCount = (
  config?: RequestConfig,
): Promise<{ unread_count: number }> => {
  return request.get('/messages/unread-count', config)
}

/**
 * 获取按联系人分组的未读消息数量
 */
export const getUnreadCountByContact = (): Promise<{ unread_counts: Record<number, number> }> => {
  return request.get('/messages/unread-count-by-contact')
}

/**
 * 获取任务相关消息
 */
export const getTaskMessages = (taskId: number): Promise<MessageResponse[]> => {
  return request.get(`/messages/task/${taskId}`)
}

// 翻译相关接口

/**
 * 翻译消息
 */
export const translateMessage = (
  messageId: number, 
  forceRetranslate: boolean = false
): Promise<MessageResponse> => {
  return request.post(`/messages/${messageId}/translate`, null, {
    params: { force_retranslate: forceRetranslate }
  })
}

/**
 * 检测消息语言
 */
export const detectLanguage = (content: string): Promise<{
  content: string
  detected_language?: string
}> => {
  return request.post('/messages/detect-language', null, {
    params: { content }
  })
}

/**
 * 获取翻译统计
 */
export const getTranslationStatistics = (days: number = 30): Promise<{
  total_translated: number
  language_pairs: Record<string, number>
  period_days: number
  user_id?: number
}> => {
  return request.get('/messages/translation/statistics', {
    params: { days }
  })
}

/**
 * 重试失败的翻译
 */
export const retryFailedTranslations = (params: {
  hours?: number
  max_messages?: number
}): Promise<{
  total_retried: number
  success_count: number
  failed_count: number
  results?: Record<string, boolean>
  error?: string
}> => {
  return request.post('/messages/translation/retry-failed', null, { params })
}

/**
 * 获取对话消息
 */
export const getConversationMessages = (
  contactId: number,
  params?: {
    page?: number
    page_size?: number
  }
): Promise<{
  items: MessageResponse[]
  total: number
  page: number
  page_size: number
  total_pages: number
  contact: UserSummary
}> => {
  return request.get(`/messages/conversation/${contactId}`, { params })
}

/**
 * 发送消息给联系人
 */
export const sendMessageToContact = (messageData: SendMessageRequest): Promise<MessageResponse> => {
  return request.post('/messages/send-to-contact', messageData)
}

// 导出消息API对象（兼容旧代码）
export const messageApi = {
  getMessages,
  getMessage,
  sendMessage,
  markMessageAsRead,
  markMessagesAsRead,
  deleteMessage,
  getUnreadCount,
  getUnreadCountByContact,
  getTaskMessages,
  translateMessage,
  detectLanguage,
  getTranslationStatistics,
  retryFailedTranslations,
  getGroupContacts,
  getConversationMessages,
  sendMessageToContact
}

// 添加缺失的导出
export const markAllMessagesAsRead = markMessagesAsRead // 别名导出

